// apps/client/src/components/auth/MfaEnrollmentCard.tsx
//
// Native GCIP TOTP enrolment card: QR + manual key + 6-digit verify + success.
// Operates on a Firebase `User` only (no app claims), so the SAME card serves
// both the enforcement-migration case (an already-claimed admin who must enrol)
// and the roleless-bootstrap case (a freshly invited user, before any role /
// companyId is granted — the backend grants claims AFTER this resolves).
//
// Failure/recovery UX: an App Check / reCAPTCHA throttle at generate/enroll time
// reuses AppCheckRecoveryBanner (the 2026-06-12 lockout lesson); a wrong code is
// an inline retry with red boxes; a network failure, rate limit or unknown error
// at verify time is a plain message with the boxes left neutral (finding M2,
// 2026-08-17: it was reported as a wrong code); the one-factor ceiling
// (`auth/maximum-second-factor-count-exceeded`) means the account is ALREADY
// enrolled, so it says so and offers Continue rather than a loop that cannot
// succeed; and an expired MFA session offers "Try again" (a fresh secret).
//
// STALE SESSION IS THE DEFAULT PATH, NOT AN ERROR (B4, founder, 2026-08-22).
// A stale session (`auth/requires-recent-login`) used to drop straight to the
// red "sign in again" box. Reproduced live at Timms: a user clicked the MFA
// on-ramp banner and was refused before anything had been attempted. For a
// rollout whose purpose is to move EXISTING users onto MFA, most sessions are
// old, so the card now re-authenticates in place first: a Google account gets
// `reauthenticateWithPopup` automatically, a password account gets an inline
// "Confirm your password" field, and either way the enrolment start is retried
// once. The red box survives unchanged as the fallback when re-auth is
// cancelled or fails. See `reauthThenRetry` for the popup/`beforeSignInMfaGate`
// reasoning.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import type { User } from 'firebase/auth';
import { Lock, Check, Info, Copy } from 'lucide-react';
import { Spinner } from '../common/Spinner';
import MfaCodeInput from './MfaCodeInput';
import {
  beginTotpEnrollment,
  completeTotpEnrollment,
  reauthenticateWithGoogle,
  reauthenticateWithPassword,
  type TotpEnrollmentInit,
} from '../../lib/nativeMfa';
import {
  ALREADY_ENROLLED_MESSAGE,
  GENERATE_ALREADY_ENROLLED_MESSAGE,
  classifyMfaFailure,
  mfaFailureMessage,
  readErrorMessage,
} from '../../lib/mfaErrors';
import {
  classifyAuthError,
  clearAppCheckThrottleState,
  type AppCheckErrorContext,
} from '../../lib/appCheckRecovery';
import AppCheckRecoveryBanner from './AppCheckRecoveryBanner';

interface MfaEnrollmentCardProps {
  user: User;
  /** Invoked when the user acknowledges the success screen (Continue). */
  onEnrolled: () => void | Promise<void>;
  /** Optional secondary action (e.g. "Sign in again" for a stale session). */
  onReauth?: () => void;
  title?: string;
  subtitle?: string;
}

type Stage =
  | 'loading'
  | 'entry'
  | 'success'
  | 'already_enrolled'
  /** B4: a Google popup re-auth is in flight; the user sees a spinner, not an error. */
  | 'reauthing'
  /** B4: a password account is being asked to confirm its password inline. */
  | 'reauth_password';

const GENERATE_FALLBACK_ERROR = 'Could not start two-factor setup. Please try again.';

/** Provider ids as they appear in `user.providerData`. */
const GOOGLE_PROVIDER_ID = 'google.com';
const PASSWORD_PROVIDER_ID = 'password';

/**
 * Which re-auth route this account supports (B4).
 *
 * Read off `providerData`, the set of credentials the account actually carries,
 * NOT `user.providerId`, which is only the last provider used. These are plain
 * object inspections with no SDK call, so they live here rather than in the
 * `nativeMfa` SDK wrapper: the card's tests mock that module at the SDK
 * boundary, and provider detection must be exercised for real, not mocked.
 */
function hasProvider(user: User, providerId: string): boolean {
  return (user.providerData ?? []).some((p) => p?.providerId === providerId);
}

const MfaEnrollmentCard: React.FC<MfaEnrollmentCardProps> = ({
  user,
  onEnrolled,
  onReauth,
  title = 'Set up two-factor authentication',
  subtitle = 'Your workspace requires a second step at sign-in. Set it up now to continue.',
}) => {
  const [stage, setStage] = useState<Stage>('loading');
  const [init, setInit] = useState<TotpEnrollmentInit | null>(null);
  const [code, setCode] = useState<string>('');
  const [verifying, setVerifying] = useState<boolean>(false);
  /** A wrong / expired code: red boxes, cleared for retry. */
  const [codeError, setCodeError] = useState<string>('');
  /** A verify-time failure that was NOT the code's fault: message only, boxes stay neutral. */
  const [verifyError, setVerifyError] = useState<string>('');
  const [fatalError, setFatalError] = useState<string>('');
  /**
   * Whether the fatal panel offers "Try again". Default true: most fatal states
   * (a dead setup session, a transient generate failure) are cleared by
   * regenerating. `email_unverified` is NOT: nothing this app can do changes
   * `emailVerified`, so a retry button there is a loop that cannot succeed, and
   * the loop is exactly the defect this card was rebuilt to remove.
   */
  const [fatalRetryable, setFatalRetryable] = useState<boolean>(true);
  /**
   * Which phase produced the `already_enrolled` stage. The copy differs:
   * nothing was scanned at generate time, so the verify-time wording would
   * describe an action the user never took.
   */
  const [alreadyEnrolledPhase, setAlreadyEnrolledPhase] =
    useState<'generate' | 'verify'>('verify');
  const [appCheckCtx, setAppCheckCtx] = useState<AppCheckErrorContext | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [resetNonce, setResetNonce] = useState<number>(0);
  const startedRef = useRef<boolean>(false);
  const alreadyEnrolledHeadingRef = useRef<HTMLHeadingElement | null>(null);
  /** B4: the password typed into the inline confirm field. Never persisted. */
  const [reauthPassword, setReauthPassword] = useState<string>('');
  const [reauthBusy, setReauthBusy] = useState<boolean>(false);
  /**
   * B4: at most ONE re-auth attempt per `start()` cycle. Without this, a
   * `requires_recent_login` that survives a successful re-auth would re-enter
   * the same branch forever. It is reset at the top of `start()` rather than
   * being permanent, so "Try again" on the fallback panel gives a user who
   * mistyped their password another go instead of a dead end.
   */
  const reauthAttemptedRef = useRef<boolean>(false);
  /**
   * B4: the ORIGINAL stale-session error, held while the inline password prompt
   * is up. If re-auth is cancelled or fails, this is what the existing fallback
   * panel reports: the reason enrolment could not start, not an SDK detail of
   * the re-auth itself.
   */
  const staleErrorRef = useRef<unknown>(null);

  const handleFailure = useCallback((err: unknown, phase: 'generate' | 'verify'): void => {
    const ctx = classifyAuthError(err);
    if (ctx.isAppCheckFailure) {
      setAppCheckCtx(ctx);
      return;
    }
    const failure = classifyMfaFailure(err);
    const message = mfaFailureMessage(failure, 'enrol');
    switch (failure.kind) {
      case 'requires_recent_login':
      case 'session_expired':
        // Both need a restart; "Try again" regenerates, "Sign in again" re-auths.
        setFatalError(message);
        return;
      case 'already_enrolled':
        // Terminal on BOTH phases, and NOT the fatal branch: the account already
        // holds its one allowed factor, so "Try again" can only fail again.
        // Generate time is the invitation-resume shape (GCIP refuses to issue a
        // second secret); verify time is the one-factor ceiling on submit. Both
        // end at Continue; only the copy differs.
        setAlreadyEnrolledPhase(phase);
        setStage('already_enrolled');
        return;
      case 'email_unverified':
        // Terminal on BOTH phases: GCIP will not begin enrolment on an
        // unverified email, and this app has no verification surface. Name the
        // blocker, offer no retry.
        setFatalRetryable(false);
        setFatalError(message);
        return;
      case 'blocked':
        // The account or the provider is off: nothing here can move past it,
        // but "Try again" stays on screen so the user is not trapped.
        setFatalError(message);
        return;
      case 'network':
      case 'rate_limited':
        if (phase === 'verify') {
          setVerifyError(message);
        } else {
          setFatalError(message);
        }
        return;
      case 'wrong_code':
        if (phase === 'verify') {
          // Wrong / expired code: stay on the entry screen, clear the boxes, retry.
          setCodeError(message);
          setResetNonce((n) => n + 1);
          setCode('');
          return;
        }
        break;
      default:
        break;
    }
    // Unknown (or a code that cannot belong to this phase): do not blame the code.
    if (phase === 'verify') {
      setVerifyError(message);
      return;
    }
    setFatalError(readErrorMessage(err) || GENERATE_FALLBACK_ERROR);
  }, []);

  /** One enrolment-start attempt, landing on the entry screen. Throws on failure. */
  const beginOnce = useCallback(async (): Promise<void> => {
    const result = await beginTotpEnrollment(user);
    setInit(result);
    setStage('entry');
  }, [user]);

  /** Route a generate-time failure to the panel that fits it. */
  const failGenerate = useCallback((err: unknown): void => {
    handleFailure(err, 'generate');
    // Do NOT clobber a terminal stage handleFailure just set. This line is
    // what used to force a generate-time `already_enrolled` back to an empty
    // entry screen, which is why that case had to fall through to the fatal
    // branch at all.
    setStage((current) => (current === 'already_enrolled' ? current : 'entry'));
  }, [handleFailure]);

  /**
   * B4: clear the stale session in place, then retry the enrolment start ONCE.
   *
   * Founder-directed after a live repro (Timms, 2026-08-22): a user with an old
   * session clicked the on-ramp banner and was met with the red "sign in again"
   * box before anything had been attempted. For a rollout whose whole point is
   * to move EXISTING users onto MFA, a stale session is the DEFAULT case, not
   * the exception, so re-authenticating is the normal path and the red box is
   * the fallback.
   *
   * POPUP CAVEAT, and why it cannot dead-end here. `reauthenticateWithPopup`
   * runs a full sign-in for the same user, so it triggers `beforeSignInMfaGate`
   * again. That gate refuses an unenrolled user only under `enforce`. At the
   * point this code runs the user is unenrolled and inside the app, which
   * leaves exactly two possibilities: the tenant is on `observe` (the gate
   * returns `no_enrolled_factor_observe` and allows), or the tenant is on
   * `enforce` and this session exists because of an exemption, which the gate
   * honours on the re-auth exactly as it did on the original sign-in. Either
   * way the re-auth is allowed. The remaining `enforce` case, a user who must
   * enrol before being let in at all, never reaches this card with a stale
   * session, because the session it would need was refused at sign-in.
   *
   * P-03 (vsec banner-polish): the enumeration above is not exhaustive, and the
   * case it misses is a tenant flipped to `enforce` BETWEEN the original sign-in
   * and this re-auth. Then the gate refuses where it previously allowed
   * (`beforeSignInMfaGate.ts:405-416`, mode `enforce` + no enrolled factor ->
   * `blockSignIn`), the popup rejects, and the catch below lands in the fallback
   * panel with the original stale-session error. No dead end, and no new one:
   * it degrades to the pre-existing `enforce` lockout for an unenrolled user,
   * which is the same outcome as signing in fresh at that moment. The general
   * statement the enumeration is an instance of: a re-auth is a sign-in for the
   * same user with the same claims, so it returns the verdict the tenant's
   * CURRENT mode gives, which is the original verdict unless the mode moved.
   *
   * Failure or cancellation falls back to the EXISTING panel, reporting the
   * ORIGINAL stale-session error rather than an SDK detail of the re-auth.
   */
  const reauthThenRetry = useCallback(
    async (reauth: () => Promise<void>, originalErr: unknown): Promise<void> => {
      setStage('reauthing');
      try {
        await reauth();
      } catch {
        failGenerate(originalErr);
        return;
      }
      try {
        await beginOnce();
      } catch (retryErr) {
        failGenerate(retryErr);
      }
    },
    [beginOnce, failGenerate],
  );

  const start = useCallback(async (): Promise<void> => {
    setStage('loading');
    setFatalError('');
    setFatalRetryable(true);
    setAlreadyEnrolledPhase('verify');
    setAppCheckCtx(null);
    setReauthPassword('');
    reauthAttemptedRef.current = false;
    staleErrorRef.current = null;
    try {
      await beginOnce();
    } catch (err) {
      // B4: a stale session is recoverable in place. Everything else, and any
      // second stale session inside the same cycle, keeps its existing panel.
      const isStale = classifyMfaFailure(err).kind === 'requires_recent_login';
      if (isStale && !reauthAttemptedRef.current) {
        reauthAttemptedRef.current = true;
        if (hasProvider(user, GOOGLE_PROVIDER_ID)) {
          await reauthThenRetry(() => reauthenticateWithGoogle(user), err);
          return;
        }
        if (hasProvider(user, PASSWORD_PROVIDER_ID)) {
          // No silent popup for a password account: ask, inline, in the card.
          staleErrorRef.current = err;
          setStage('reauth_password');
          return;
        }
      }
      failGenerate(err);
    }
  }, [user, beginOnce, failGenerate, reauthThenRetry]);

  /** B4: submit the inline password, then retry the enrolment start once. */
  const handleReauthPassword = useCallback(async (): Promise<void> => {
    if (!reauthPassword || reauthBusy) return;
    const password = reauthPassword;
    setReauthBusy(true);
    try {
      await reauthThenRetry(
        () => reauthenticateWithPassword(user, password),
        staleErrorRef.current,
      );
    } finally {
      setReauthBusy(false);
      setReauthPassword('');
    }
  }, [reauthPassword, reauthBusy, user, reauthThenRetry]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void start();
  }, [start]);

  // The already-enrolled panel replaces the whole card, so keyboard and screen
  // reader focus must follow it rather than stay on a button that is gone.
  useEffect(() => {
    if (stage === 'already_enrolled') {
      alreadyEnrolledHeadingRef.current?.focus();
    }
  }, [stage]);

  const handleVerify = useCallback(async (submitCode: string = code): Promise<void> => {
    if (!init || submitCode.length !== 6 || verifying) return;
    setVerifying(true);
    setCodeError('');
    setVerifyError('');
    try {
      await completeTotpEnrollment(user, init.secret, submitCode);
      setStage('success');
    } catch (err) {
      handleFailure(err, 'verify');
    } finally {
      setVerifying(false);
    }
  }, [init, code, verifying, user, handleFailure]);

  const handleCopyKey = useCallback(async (): Promise<void> => {
    if (!init) return;
    try {
      await navigator.clipboard.writeText(init.sharedSecretKey.replace(/\s/g, ''));
    } catch {
      // Clipboard unavailable — the key is visible for manual entry.
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, [init]);

  // -- App Check throttle: reuse the shared recovery banner -------------------
  if (appCheckCtx) {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700">
        <AppCheckRecoveryBanner
          ctx={appCheckCtx}
          onReset={async () => {
            await clearAppCheckThrottleState();
            window.location.reload();
          }}
        />
      </div>
    );
  }

  // -- Already enrolled (the one-factor ceiling, at generate OR verify time) ---------------
  if (stage === 'already_enrolled') {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mb-4">
          <Check size={30} className="text-white" />
        </div>
        <h2
          ref={alreadyEnrolledHeadingRef}
          tabIndex={-1}
          className="text-2xl font-bold text-white mb-2 focus:outline-none"
        >
          Two-factor authentication is already on
        </h2>
        {/* role="status" (polite by default): the panel already took focus, so
            an assertive live region would interrupt the heading announcement. */}
        <p className="text-gray-400 text-sm mb-6 leading-relaxed" role="status">
          {alreadyEnrolledPhase === 'generate'
            ? GENERATE_ALREADY_ENROLLED_MESSAGE
            : ALREADY_ENROLLED_MESSAGE}
        </p>
        <button
          type="button"
          onClick={() => void onEnrolled()}
          className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white p-3 rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          Continue to VALUTTEN
        </button>
      </div>
    );
  }

  // -- Success ----------------------------------------------------------------
  if (stage === 'success') {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mb-4">
          <Check size={30} className="text-white" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Two-factor authentication is on</h2>
        <p className="text-gray-400 text-sm mb-6 leading-relaxed">
          Your account now asks for a code at every sign-in. That&rsquo;s the whole setup.
        </p>
        <div className="bg-blue-900/30 border border-blue-500/40 text-blue-100 p-4 rounded-lg mb-6 flex items-start text-sm leading-relaxed">
          <Info size={18} className="mr-3 flex-shrink-0 mt-0.5 text-blue-300" />
          <span>
            Keep your authenticator app safe. If you lose the device, use{' '}
            <b className="text-white">Can&rsquo;t access your authenticator?</b> at sign-in to get
            your two-factor setup reset by Valutten.
          </span>
        </div>
        <button
          type="button"
          onClick={() => void onEnrolled()}
          className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white p-3 rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          Continue to VALUTTEN
        </button>
      </div>
    );
  }

  // -- Enrolment (loading + entry) --------------------------------------------
  return (
    <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mb-4">
          <Lock size={30} className="text-white" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">{title}</h2>
        <p className="text-gray-400 text-sm leading-relaxed">{subtitle}</p>
      </div>

      {/* The scan instructions describe the NEXT screen, so they are held back
          while a re-auth is in flight or the password prompt is up (B4): at
          that point there is no code to scan yet. */}
      {stage !== 'reauthing' && stage !== 'reauth_password' && (
        <div className="bg-blue-900/30 border border-blue-500/40 text-blue-100 p-4 rounded-lg mb-6 flex items-start text-sm leading-relaxed">
          <Info size={18} className="mr-3 flex-shrink-0 mt-0.5 text-blue-300" />
          <span>
            Scan the code with an authenticator app (Google Authenticator, 1Password, Authy).
            You&rsquo;ll enter a fresh 6-digit code each time you sign in.
          </span>
        </div>
      )}

      {fatalError && (
        <div
          className="bg-red-900/50 border border-red-500 text-red-200 p-4 rounded-lg mb-6 text-sm"
          role="alert"
          aria-live="assertive"
        >
          <p>{fatalError}</p>
          <div className="mt-3 flex gap-3">
            {fatalRetryable && (
              <button
                type="button"
                onClick={() => void start()}
                className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2"
              >
                Try again
              </button>
            )}
            {onReauth && (
              <button
                type="button"
                onClick={onReauth}
                className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2"
              >
                Sign in again
              </button>
            )}
          </div>
        </div>
      )}

      {stage === 'loading' && (
        <div className="flex items-center justify-center py-10 text-gray-400">
          <Spinner size="sm" className="mr-2" />
          <span className="text-sm">Preparing your setup code&hellip;</span>
        </div>
      )}

      {/* B4: Google re-auth in flight. The popup is the interaction; this is
          just the card saying why it is waiting, instead of a red box. */}
      {stage === 'reauthing' && (
        <div
          className="flex items-center justify-center py-10 text-gray-400"
          role="status"
          aria-live="polite"
        >
          <Spinner size="sm" className="mr-2" />
          <span className="text-sm">Confirming it&rsquo;s you&hellip;</span>
        </div>
      )}

      {/* B4: password account, stale session. Confirm in place. */}
      {stage === 'reauth_password' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleReauthPassword();
          }}
        >
          <p className="text-sm text-gray-400 leading-relaxed mb-4">
            You have been signed in for a while. Confirm your password to start setting up
            two-factor authentication.
          </p>
          <label
            htmlFor="mfa-reauth-password"
            className="block mb-2 text-sm font-medium text-gray-300"
          >
            Confirm your password
          </label>
          <input
            id="mfa-reauth-password"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={reauthPassword}
            onChange={(e) => setReauthPassword(e.target.value)}
            disabled={reauthBusy}
            className="w-full bg-gray-700/50 border border-gray-600 text-white text-sm rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent block p-3 mb-5 transition-all duration-200 disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!reauthPassword || reauthBusy}
            aria-busy={reauthBusy}
            className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white p-3 rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {reauthBusy ? (
              <>
                <Spinner size="sm" className="mr-2" />
                <span>Confirming&hellip;</span>
              </>
            ) : (
              <span>Confirm and continue</span>
            )}
          </button>
        </form>
      )}

      {stage === 'entry' && init && (
        <>
          <div className="flex gap-5 items-center mb-6 flex-col sm:flex-row">
            <div className="flex-none bg-white rounded-xl p-2.5">
              <QRCodeSVG value={init.otpauthUrl} size={128} />
            </div>
            <div className="text-sm text-gray-400 leading-relaxed">
              Can&rsquo;t scan? Enter this key by hand:
              <div className="mt-2 flex items-center justify-between gap-2 font-mono text-sm text-white bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 tracking-wider">
                <span className="break-all">{init.sharedSecretKey}</span>
                <button
                  type="button"
                  onClick={() => void handleCopyKey()}
                  className="flex-none inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-xs"
                  aria-label="Copy setup key"
                >
                  <Copy size={13} />
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </div>

          <label className="block mb-2 text-sm font-medium text-gray-300">
            Enter the 6-digit code from your app
          </label>
          <MfaCodeInput
            ariaLabel="Setup verification code"
            onChange={setCode}
            onComplete={(v) => void handleVerify(v)}
            disabled={verifying}
            error={!!codeError}
            resetNonce={resetNonce}
          />
          <p
            className={`text-xs text-center mt-2 mb-5 min-h-[1rem] ${codeError || verifyError ? 'text-red-400' : 'text-gray-500'}`}
            role={codeError || verifyError ? 'alert' : undefined}
            aria-live={codeError || verifyError ? 'assertive' : undefined}
          >
            {codeError || verifyError || '6 digits from your authenticator'}
          </p>

          <button
            type="button"
            onClick={() => void handleVerify()}
            disabled={code.length !== 6 || verifying}
            className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white p-3 rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
            aria-busy={verifying}
          >
            {verifying ? (
              <>
                <Spinner size="sm" className="mr-2" />
                <span>Verifying&hellip;</span>
              </>
            ) : (
              <span>Verify and turn on</span>
            )}
          </button>
        </>
      )}
    </div>
  );
};

export default MfaEnrollmentCard;
